import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuMessageSquare, LuUsers, LuInbox, LuShare2 } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../landing.css';

const FEATURES = [
  { icon: <LuMessageSquare size={16} strokeWidth={2} />, title: 'Two-way conversations', body: 'Real chat threads, not one-shot blasts. Replies come straight to the inbox in real time.' },
  { icon: <LuUsers size={16} strokeWidth={2} />, title: 'Shared contacts', body: 'Build a contact list once, reuse it everywhere — bulk campaigns, dispatch, customer support.' },
  { icon: <LuInbox size={16} strokeWidth={2} />, title: 'Unified inbox', body: 'All conversations from all your SIMs in one place. Filter, search, mark read.' },
  { icon: <LuShare2 size={16} strokeWidth={2} />, title: 'Multi-user', body: 'Add team members as sub-users. Assign SIMs per agent. Everyone sees their own threads.' },
];

export default function WebTextingApp() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="Web Texting App — Send SMS From Your Browser"
        description="TextLink's web SMS app turns your browser into a full texting client. Two-way conversations, shared contacts, multi-user inbox, attachments. Your SIM, on every device."
        canonical="/web-sms-app"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Web Texting App</span>
            <h1 className="lp-h1">
              Text from your browser.<br />
              <span className="lp-h1-accent">Your SIM, on every device.</span>
            </h1>
            <p className="lp-lead">
              A full SMS client in the browser. Real two-way conversations, shared contacts, unified inbox — perfect for dispatchers, sales teams, and anyone tired of typing long messages on a tiny phone keyboard.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
            <div className="lp-meta">
              <span>300 free messages</span>
              <span className="lp-meta-sep" />
              <span>Multi-user</span>
              <span className="lp-meta-sep" />
              <span>Real-time delivery</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">Built for teams that text all day</h2>
            <p className="lp-sub">Everything an SMS client should have, in the browser.</p>
            <ul className="lp-feat-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{f.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{f.title}</strong>
                    <span>{f.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Who it's for</h2>
            <p className="lp-sub">High-volume two-way SMS workflows.</p>
            <div className="lp-grid-3">
              <div className="lp-card">
                <h3 className="lp-card-h">Trucking dispatch</h3>
                <p className="lp-card-p">Coordinate driver assignments and load updates from a desktop while juggling phone calls.</p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">Sales / SDRs</h3>
                <p className="lp-card-p">Reply to inbound leads at desktop typing speed. Templates and saved responses for the common ones.</p>
              </div>
              <div className="lp-card">
                <h3 className="lp-card-h">Real-time customer support</h3>
                <p className="lp-card-p">Resolve tickets over SMS without bouncing between phone screens. Threaded by contact, not by chronology.</p>
              </div>
            </div>
            <div className="lp-callout">
              <strong>Live preview:</strong>{' '}
              <a href="https://textlinksms.com/dashboard" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                See the dashboard
              </a>{' '}
              after signup — the web texting app is built into every account.
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Stop typing on your phone.</h2>
            <p className="lp-sub">Sign up, pair a SIM, start texting from desktop. Five minutes.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/Contact">Talk to us</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
